// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import {
  DEFAULT_LIGHTING,
  lightDirection,
  orthoHalfHeight,
  presetOrientation,
  type CameraProjection,
  type LightingState,
  type ViewPreset,
} from './cameraViews';
import { labelColorIndex, slotColor, paletteToFloats, readLabelPalette } from './labelColors';
import { MIN_GHOST_OPACITY, applyLut, compileLut, identityOrder, type ColorLut, type CompiledLut, type GhostStyle, type Rgb } from './colorLut';
import { clipPlanesChanged, fitClipPlanes, shouldRefollow } from './clipPlanes';
import { isCameraSnapshot, type CameraSnapshot } from './viewerCache';

export type { ColorLut, GhostStyle };

/**
 * `rgb`: sensor colour. `label`: the categorical palette over the page's
 * primary label cloud. `lut`: the Ground's per-instance table (`setColorLut`).
 */
export type ColorMode = 'rgb' | 'label' | 'lut';

/** How `setPointSize` reads its value: world metres (attenuated) or screen pixels. */
export type PointSizeUnit = 'm' | 'px';

/** Pick radius in world metres when points are sized in pixels. */
const PX_PICK_THRESHOLD = 0.04;

/** Ghost style before the viewport has read the tokens (mid grey on light stone). */
const DEFAULT_GHOST: GhostStyle = { surface: [0.83, 0.83, 0.81], ghost: [0.27, 0.28, 0.3], alpha: 0.28 };

/** Ghost points' opacity until the viewport sets one: opaque, the expert viewports' behaviour. */
const DEFAULT_GHOST_OPACITY = 1;

/** Ghost passes draw after every solid one (see `Page.ghostPoints`). */
const GHOST_RENDER_ORDER = 1;

/** Oblique framing direction: an axis view of a corridor has no depth cue. */
const OBLIQUE_DIRECTION = new THREE.Vector3(0.7, -0.7, 0.45).normalize();

/** World up for the gravity-aligned scan. */
const WORLD_UP = new THREE.Vector3(0, 0, 1);

/**
 * Radius of the panorama backdrop, metres.
 *
 * Far enough that a room's geometry fits inside it — a sphere smaller than the
 * scan would clip through the walls the user is comparing it against — and
 * near enough to stay well inside the far plane at ordinary building scale.
 */
export const PANORAMA_RADIUS = 20;

/**
 * The locked plan's camera: exactly straight down, +Y up the page. Unlike
 * the `top` preset it does not lean off the pole (`POLE_LEAN` in
 * `cameraViews.ts`): that lean keeps the orbit frame right for a later
 * orbit, and the plan never orbits — its up vector, perpendicular to the
 * view, is put back to Z when the lock comes off.
 */
function planOrientation(): [THREE.Vector3, THREE.Vector3] {
  return [new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 1, 0)];
}

/** Fallback look direction when a caller supplies a degenerate one. */
const FORWARD_X = new THREE.Vector3(1, 0, 0);

/** The orbit camera as it was before panorama mode took it over. */
interface SavedCamera {
  position: THREE.Vector3;
  target: THREE.Vector3;
  up: THREE.Vector3;
  near: number;
  far: number;
  fov: number;
  projection: CameraProjection;
}

/** The orbit camera as it was before the locked plan took it over. */
interface PlanRestore {
  position: THREE.Vector3;
  target: THREE.Vector3;
  up: THREE.Vector3;
  /** The projection to go back to; a request made while locked lands here. */
  projection: CameraProjection;
  /** The projection the pose and frustum below were taken under. */
  takenUnder: CameraProjection;
  /** The orthographic frustum's half-height and zoom, when that was the projection. */
  orthoHalfHeight: number;
  orthoZoom: number;
}

/** One decoded page, in the form the scene consumes. */
export interface PageBuffers {
  /** xyz triples, in world coordinates as stored. */
  positions: Float32Array;
  /** rgb triples in 0..1, or null for a cloud with no colour. */
  rgb: Float32Array | null;
  /**
   * Per-point label values of the primary (first) label source, or null when
   * none is selected. Drives the `'label'` colour mode.
   */
  labels: Uint32Array | null;
  /**
   * Every zipped label source by `Label` cloud name (`instances`, `rooms`, …),
   * index-aligned with `positions`. Drives the `'lut'` colour mode and the
   * Ground's instance index. Absent for a single-source stream.
   */
  labelsBy?: Record<string, Uint32Array>;
}

/**
 * One page on the GPU, drawn in two passes over the same position and colour
 * buffers (frontend-r22.22): `points` the solid points, opaque and writing
 * depth; `ghostPoints` the ghost ones (unlabelled, unowned, outside a room or
 * storey focus) see-through, after every solid pass and without writing
 * depth, so a labelled point behind a ghost one still shows. Both draw
 * through one index buffer (`order`) that `writeColors` partitions — solid
 * indices first, ghost after — and each pass takes its range of it.
 */
interface Page {
  points: THREE.Points;
  ghostPoints: THREE.Points;
  order: THREE.BufferAttribute;
  /** Whether `order` is the identity (no ghost): a mode switch between RGB and label skips the re-upload. */
  identity: boolean;
  rgb: Float32Array | null;
  labels: Uint32Array | null;
  labelsBy: Record<string, Uint32Array> | null;
}

interface Layer {
  group: THREE.Group;
  pages: Page[];
}

/**
 * Imperative three.js scene for streamed point clouds.
 *
 * Kept entirely outside React on purpose. A cloud arrives as dozens to hundreds
 * of pages, and re-rendering a component tree per page — or holding GPU buffers
 * in component state — would make React's reconciler the bottleneck in
 * something that is really just "append a buffer and redraw". The React side
 * (`Viewport.tsx`) owns the canvas element and the lifecycle; everything after
 * that is method calls.
 *
 * ## Coordinate handling
 *
 * Positions are recentred on the first page's bounding-box centre before they
 * reach the GPU. Scans can be georeferenced, and a `Float32Array` holding
 * coordinates in the hundreds of thousands of metres has decimetre resolution
 * left — the cloud visibly quantises onto a lattice and the depth buffer
 * shatters. The library has the same problem and solves it the same way
 * (`CellComplex` recentres to its bbox centroid, STANDARDS §4). The offset is
 * fixed by the first page so that later pages stay registered to it.
 */
export class PointCloudScene {
  private readonly canvas: HTMLCanvasElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  // Editing gizmos (the clipping box) draw here, in a second pass with the
  // clipping planes off: a gizmo that edits the clip region must not itself
  // be clipped by it (frontend-h1x).
  private readonly overlay = new THREE.Scene();
  private readonly perspectiveCamera: THREE.PerspectiveCamera;
  private readonly orthographicCamera: THREE.OrthographicCamera;
  private camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  private projection: CameraProjection = 'perspective';
  private readonly controls: OrbitControls;
  private readonly layers = new Map<string, Layer>();

  // Viewport lighting rig (#443). One key + one hemisphere fill live on the
  // scene, not per-mesh: a viewport has one light model, adjustable from the
  // panel and unchanged by which meshes are toggled on. Points and splats draw
  // with unlit materials, so the lights only ever reach a lit mesh.
  private readonly hemisphere: THREE.HemisphereLight;
  private readonly keyLight: THREE.DirectionalLight;
  private lighting: LightingState = { ...DEFAULT_LIGHTING };

  private readonly bounds = new THREE.Box3();
  private origin: THREE.Vector3 | null = null;

  private saved: SavedCamera | null = null;
  // The locked plan (frontend-ayw.1): orthographic, top-down, pan and zoom
  // only. `planRestore` is the camera to go back to; a lock asked for inside
  // a panorama waits in `pendingPlanLock` for the way out.
  private planLocked = false;
  private planRestore: PlanRestore | null = null;
  private pendingPlanLock: boolean | null = null;
  // A horizontal section (the locked plan's storey cut): world z kept below,
  // combined with whatever the clipping box asks for.
  private sectionWorldZ: number | null = null;
  private sectionPlane: THREE.Plane | null = null;
  private boxPlanes: THREE.Plane[] = [];
  private colorMode: ColorMode = 'rgb';
  private pointSize = 0.02;
  private pointSizeUnit: PointSizeUnit = 'm';
  private lut: ColorLut | null = null;
  private ghostStyle: GhostStyle = DEFAULT_GHOST;
  private ghostOpacity = DEFAULT_GHOST_OPACITY;
  private compiled: CompiledLut | null = null;
  private frameHandle: number | null = null;
  // Automatic first framing keeps up with the streaming bounds until the user
  // takes the camera (frontend-r22.19): radius it last framed, and when.
  private following = false;
  private followedRadius = 0;
  private lastFollowAt = 0;
  private resizeObserver: ResizeObserver | null = null;
  private disposed = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.perspectiveCamera = new THREE.PerspectiveCamera(60, 1, 0.01, 5000);
    // Building scans are gravity-aligned with Z up; three.js defaults to Y up,
    // which would make the orbit gimbal fight the data on every drag.
    this.perspectiveCamera.up.copy(WORLD_UP);
    this.perspectiveCamera.position.set(6, -6, 4);

    // Orthographic peer. Its frustum is (re)sized from the perspective view on
    // every projection toggle and reframing, so these placeholder extents are
    // overwritten before it is ever the active camera.
    this.orthographicCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 5000);
    this.orthographicCamera.up.copy(WORLD_UP);
    this.orthographicCamera.position.copy(this.perspectiveCamera.position);

    this.camera = this.perspectiveCamera;

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    this.controls.screenSpacePanning = true;
    this.applyControlMapping();
    // The user taking the camera ends the automatic framing.
    this.controls.addEventListener('start', this.stopFollowing);

    this.hemisphere = new THREE.HemisphereLight(0xdfe4ec, 0x14171c, this.lighting.ambientIntensity);
    this.hemisphere.position.copy(WORLD_UP);
    this.keyLight = new THREE.DirectionalLight(0xffffff, this.lighting.keyIntensity);
    this.scene.add(this.hemisphere);
    this.scene.add(this.keyLight);
    this.applyLighting();

    this.applyBackgroundFromTokens();
    this.observeResize(canvas);
    this.loop();
  }

  /**
   * Take the canvas colour from `--color-canvas` rather than hard-coding it.
   *
   * The viewport background is a design token like any other; reading it here
   * is what keeps the 3D view inside the design system instead of beside it.
   */
  private applyBackgroundFromTokens(): void {
    this.scene.background = this.resolveColorToken('--color-canvas', '#0a0b0d');
  }

  /**
   * Clear to another token, re-read now (call again after a theme switch).
   *
   * The Ground clears to `--color-surface` in both themes (PLAN.md decision
   * 5), so its canvas is not pinned to the dark scope and follows the app
   * theme; the expert viewports keep `--color-canvas`.
   */
  applyBackgroundToken(name: string): void {
    this.scene.background = this.resolveColorToken(name, '#0a0b0d');
  }

  /**
   * Resolve a CSS custom property to a {@link THREE.Color}, from the canvas's
   * own cascade so themed overrides apply.
   *
   * Shared with sibling layers ({@link MeshScene}) that draw into this scene and
   * must take their colours from the same design tokens rather than hard-coding
   * them. Falls back to @p fallback when the property is unset or empty.
   */
  resolveColorToken(name: string, fallback: string): THREE.Color {
    const value = getComputedStyle(this.canvas).getPropertyValue(name).trim();
    return new THREE.Color(value || fallback);
  }

  private observeResize(canvas: HTMLCanvasElement): void {
    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const { clientWidth, clientHeight } = parent;
      if (clientWidth === 0 || clientHeight === 0) return;
      this.renderer.setSize(clientWidth, clientHeight, false);
      const aspect = clientWidth / clientHeight;
      this.perspectiveCamera.aspect = aspect;
      this.perspectiveCamera.updateProjectionMatrix();
      // Hold the orthographic vertical extent and refit the horizontal one, so
      // a resize never changes how tall the scene reads under either camera.
      const halfHeight = (this.orthographicCamera.top - this.orthographicCamera.bottom) / 2;
      this.orthographicCamera.left = -halfHeight * aspect;
      this.orthographicCamera.right = halfHeight * aspect;
      this.orthographicCamera.updateProjectionMatrix();
    };
    resize();
    this.resizeObserver = new ResizeObserver(resize);
    if (canvas.parentElement) this.resizeObserver.observe(canvas.parentElement);
  }

  private loop = (): void => {
    if (this.disposed) return;
    this.frameHandle = requestAnimationFrame(this.loop);
    // A section asked for before any content waits for the recentring origin.
    if (this.sectionWorldZ !== null && this.sectionPlane === null && this.origin) this.applyClipping();
    this.followBounds();
    this.controls.update();
    this.fitClipPlanes();
    this.renderer.render(this.scene, this.camera);
    if (this.overlay.children.length > 0) this.renderOverlay();
  };

  private readonly stopFollowing = (): void => {
    this.following = false;
  };

  /**
   * Redo the automatic first framing when the loaded bounds have grown past
   * what it framed (the first page of a Morton-ordered cloud is one corner of
   * the building), at most twice a second so the camera does not twitch per
   * page. Ends as soon as the user orbits, pans or zooms, or anything else
   * frames the camera.
   */
  private followBounds(): void {
    if (!this.following || this.bounds.isEmpty()) return;
    const now = performance.now();
    if (now - this.lastFollowAt < 500) return;
    const radius = this.bounds.getBoundingSphere(new THREE.Sphere()).radius;
    if (!shouldRefollow(this.followedRadius, radius)) return;
    this.frameFromDirection(...this.homeOrientation());
    this.followedRadius = radius;
    this.lastFollowAt = now;
  }

  /**
   * Fit the active camera's near/far planes to the bounding sphere of
   * everything loaded, from where the camera stands now (frontend-r22.19).
   *
   * Run every frame, so bounds growth, dolly, orbit, a focus, a projection
   * toggle and the return from a panorama all get planes that cover the whole
   * scene; the matrix is only rebuilt when a plane moves by more than 1 %.
   * Panorama mode keeps its own fixed planes (the camera is inside a 20 m
   * backdrop, not looking at the building).
   */
  private fitClipPlanes(): void {
    if (this.saved || this.bounds.isEmpty() || !this.origin) return;
    const sphere = this.bounds.getBoundingSphere(new THREE.Sphere());
    sphere.center.sub(this.origin);
    const cam = this.camera;
    const next = fitClipPlanes(cam.position.distanceTo(sphere.center), sphere.radius, this.projection);
    if (!clipPlanesChanged({ near: cam.near, far: cam.far }, next)) return;
    cam.near = next.near;
    cam.far = next.far;
    cam.updateProjectionMatrix();
  }

  /** Draw {@link overlay} over the main pass, unclipped, sharing its depth. */
  private renderOverlay(): void {
    const planes = this.renderer.clippingPlanes;
    this.renderer.clippingPlanes = [];
    this.renderer.autoClear = false;
    this.renderer.render(this.overlay, this.camera);
    this.renderer.autoClear = true;
    this.renderer.clippingPlanes = planes;
  }

  /** Append one decoded page to a layer, creating the layer if needed. */
  addPage(layerId: string, page: PageBuffers): void {
    if (this.disposed) return;

    const count = Math.floor(page.positions.length / 3);
    if (count === 0) return;

    if (!this.origin) this.origin = centroidOf(page.positions);
    const local = recentre(page.positions, this.origin);

    const position = new THREE.BufferAttribute(local, 3);
    const color = new THREE.BufferAttribute(new Float32Array(count * 3), 3);
    const order = new THREE.BufferAttribute(new Uint32Array(count), 1);
    identityOrder(order.array as Uint32Array, count);

    // Two geometries over shared attributes: three.js uploads an attribute
    // once however many geometries hold it, and each pass needs its own draw
    // range of the one index buffer.
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', position);
    geometry.setAttribute('color', color);
    geometry.setIndex(order);
    geometry.computeBoundingSphere();
    const ghostGeometry = new THREE.BufferGeometry();
    ghostGeometry.setAttribute('position', position);
    ghostGeometry.setAttribute('color', color);
    ghostGeometry.setIndex(order);
    ghostGeometry.boundingSphere = geometry.boundingSphere?.clone() ?? null;

    const points = new THREE.Points(geometry, this.solidMaterial());
    const ghostPoints = new THREE.Points(ghostGeometry, this.ghostMaterial());
    ghostPoints.renderOrder = GHOST_RENDER_ORDER;
    const record: Page = {
      points,
      ghostPoints,
      order,
      identity: true,
      rgb: page.rgb,
      labels: page.labels,
      labelsBy: page.labelsBy ?? null,
    };
    this.writeColors(record);

    let layer = this.layers.get(layerId);
    if (!layer) {
      layer = { group: new THREE.Group(), pages: [] };
      this.scene.add(layer.group);
      this.layers.set(layerId, layer);
    }
    layer.group.add(points);
    layer.group.add(ghostPoints);
    layer.pages.push(record);

    this.growBounds(page.positions);
  }

  /**
   * Fill a page's colour attribute for the current mode.
   *
   * Colours are recomputed from the retained source arrays rather than refetched
   * when the mode changes: a 20-million-point cloud costs one pass over memory
   * to recolour and a full reload to refetch, and the user toggling RGB/label to
   * compare them is the normal way this control gets used.
   */
  private writeColors(page: Page): void {
    const attribute = page.points.geometry.getAttribute('color') as THREE.BufferAttribute;
    const target = attribute.array as Float32Array;
    const count = attribute.count;
    // Solid points in the order buffer, and the points drawn at all (solid +
    // ghost; the rest are hidden by a room focus); null = no partition (every
    // point solid).
    let ranges: { solid: number; drawn: number } | null = null;

    if (this.colorMode === 'lut' && this.lut && this.compiled) {
      // The Ground's per-instance table: one lookup per point (two with a
      // room or storey focus; `secondary.keep` is one room id or a storey's
      // list, `applyLut` handles both). A page without the table's label cloud — the stream has
      // none for this layer — reads as unlabeled, i.e. ghost, not as RGB:
      // mixing sensor colour into a ghosted building would read as emphasis.
      // `secondary.hide` (a room focus without "Vis omgivelser") leaves the
      // points outside the room out of both passes. Only this layer is
      // room-labelled: a mesh or splat still shows the surroundings.
      const lut = this.lut;
      const labels = page.labelsBy?.[lut.cloud] ?? null;
      const roomLabels = lut.secondary ? (page.labelsBy?.[lut.secondary.cloud] ?? null) : null;
      ranges = applyLut(
        target,
        count,
        this.compiled,
        labels,
        lut.secondary && roomLabels ? { labels: roomLabels, keep: lut.secondary.keep } : null,
        page.order.array as Uint32Array,
        lut.secondary?.hide ?? false,
      );
      page.order.needsUpdate = true;
      page.identity = false;
    } else if (this.colorMode === 'label' && page.labels) {
      // From the canvas's own cascade (pinned to the dark token scope,
      // Viewport.tsx), not the document's active theme: `--label-invalid` is
      // the one label token that legitimately differs per theme (chosen for a
      // light panel, not for this always-dark surface), and it must recede
      // here regardless of which app theme is active (integration review
      // finding 3).
      const palette = paletteToFloats(readLabelPalette(this.canvas));
      for (let i = 0; i < count; i += 1) {
        const slot = labelColorIndex(page.labels[i], palette.colors.length);
        const [r, g, b] = slotColor(palette, slot);
        target[i * 3] = r;
        target[i * 3 + 1] = g;
        target[i * 3 + 2] = b;
      }
    } else if (page.rgb) {
      target.set(page.rgb.subarray(0, count * 3));
    } else {
      // A PointXYZ cloud has no colour of its own. Flat neutral rather than
      // white: white points on a near-black canvas bloom and hide structure.
      const [r, g, b] = paletteToFloats(readLabelPalette(this.canvas)).unlabeled;
      for (let i = 0; i < count; i += 1) {
        target[i * 3] = r;
        target[i * 3 + 1] = g;
        target[i * 3 + 2] = b;
      }
    }
    if (ranges === null) {
      ranges = { solid: count, drawn: count };
      if (!page.identity) {
        identityOrder(page.order.array as Uint32Array, count);
        page.order.needsUpdate = true;
        page.identity = true;
      }
    }
    const { solid, drawn } = ranges;
    attribute.needsUpdate = true;
    page.points.geometry.setDrawRange(0, solid);
    page.ghostPoints.geometry.setDrawRange(solid, drawn - solid);
    page.ghostPoints.visible = drawn > solid;
  }

  /** The solid pass's material: opaque, writes depth. */
  private solidMaterial(): THREE.PointsMaterial {
    return new THREE.PointsMaterial({
      size: this.pointSize,
      sizeAttenuation: this.pointSizeUnit === 'm',
      vertexColors: true,
    });
  }

  /** The ghost pass's material: see-through below opacity 1, never writes depth then. */
  private ghostMaterial(): THREE.PointsMaterial {
    const material = this.solidMaterial();
    this.styleGhostMaterial(material);
    return material;
  }

  private styleGhostMaterial(material: THREE.PointsMaterial): void {
    const seeThrough = this.ghostOpacity < 1;
    if (material.transparent !== seeThrough) material.needsUpdate = true;
    material.transparent = seeThrough;
    material.opacity = this.ghostOpacity;
    material.depthWrite = !seeThrough;
  }

  /**
   * How opaque ghost points draw, 0..1 (the Ground's "Gennemsigtighed for
   * umærkede punkter"). Below 1 they draw see-through without writing depth,
   * after every solid point; 1 is the old opaque ghost. A material change
   * only: no recolour, so a slider drag stays cheap on a big cloud.
   */
  setGhostOpacity(opacity: number): void {
    const next = Number.isFinite(opacity) ? Math.min(1, Math.max(MIN_GHOST_OPACITY, opacity)) : 1;
    if (next === this.ghostOpacity) return;
    this.ghostOpacity = next;
    for (const layer of this.layers.values()) {
      for (const page of layer.pages) this.styleGhostMaterial(page.ghostPoints.material as THREE.PointsMaterial);
    }
  }

  setColorMode(mode: ColorMode): void {
    if (this.colorMode === mode) return;
    this.colorMode = mode;
    this.recolorAll();
  }

  currentColorMode(): ColorMode {
    return this.colorMode;
  }

  private recolorAll(): void {
    for (const layer of this.layers.values()) {
      for (const page of layer.pages) this.writeColors(page);
    }
  }

  /**
   * The table the `'lut'` mode colours by (`colorLut.ts`). Compiled against
   * the current ghost style once here, then every resident page is recoloured
   * — one pass over memory, the same cost as a colour-mode toggle.
   */
  setColorLut(lut: ColorLut | null): void {
    this.lut = lut;
    this.compiled = lut ? compileLut(lut, this.ghostStyle) : null;
    if (this.colorMode === 'lut') this.recolorAll();
  }

  /**
   * The surface, ghost colour and ghost alpha ghosting mixes with. The viewport
   * reads them from the tokens (and again after a theme switch) and passes
   * them in, so this class never guesses a theme.
   */
  setGhostStyle(style: GhostStyle): void {
    const same =
      style.alpha === this.ghostStyle.alpha &&
      style.surface.every((v, i) => v === this.ghostStyle.surface[i]) &&
      style.ghost.every((v, i) => v === this.ghostStyle.ghost[i]);
    if (same) return;
    this.ghostStyle = style;
    if (this.lut) this.compiled = compileLut(this.lut, style);
    if (this.colorMode === 'lut') this.recolorAll();
  }

  /**
   * Point size, in world metres (attenuated with distance — the expert
   * viewport's default) or in screen pixels (a fixed dot, as the Ground's
   * Størrelse S/M/L).
   */
  setPointSize(size: number, unit: PointSizeUnit = 'm'): void {
    const unitChanged = unit !== this.pointSizeUnit;
    this.pointSize = size;
    this.pointSizeUnit = unit;
    for (const layer of this.layers.values()) {
      for (const page of layer.pages) {
        for (const points of [page.points, page.ghostPoints]) {
          const material = points.material as THREE.PointsMaterial;
          material.size = size;
          if (unitChanged) {
            material.sizeAttenuation = unit === 'm';
            // Attenuation is a shader define: the program must be rebuilt.
            material.needsUpdate = true;
          }
        }
      }
    }
  }

  setLayerVisible(layerId: string, visible: boolean): void {
    const layer = this.layers.get(layerId);
    if (layer) layer.group.visible = visible;
  }

  // --- camera projection (#443) -------------------------------------------

  /** Whether the orbit camera is currently perspective or orthographic. */
  currentProjection(): CameraProjection {
    return this.projection;
  }

  /**
   * Switch the orbit camera between perspective and orthographic, preserving
   * the framing the user was looking at.
   *
   * The two cameras share a target and view direction; only the projection
   * differs. Going to orthographic, the frustum is sized to show the same
   * vertical extent the perspective camera spanned at the target distance;
   * going back, the perspective camera is stood off at the distance that
   * reproduces the orthographic camera's visible height. Either way the scene
   * stays the same size at the plane the user was focused on, so the switch
   * reads as a change of projection rather than a jump.
   *
   * In panorama mode the look-around is always perspective, so a request there
   * is only remembered and applied when the orbit camera returns
   * (see {@link exitFirstPerson}).
   */
  setProjection(next: CameraProjection): void {
    // The locked plan is orthographic by definition: remember the choice for
    // when it is unlocked.
    if (this.planLocked) {
      if (this.planRestore) this.planRestore.projection = next;
      return;
    }
    this.applyProjection(next);
  }

  /** {@link setProjection} without the plan-lock guard (the lock itself, the way out of a panorama). */
  private applyProjection(next: CameraProjection): void {
    if (this.projection === next) return;
    if (this.saved) {
      this.saved.projection = next;
      return;
    }

    const target = this.controls.target;
    const persp = this.perspectiveCamera;
    const ortho = this.orthographicCamera;

    if (next === 'orthographic') {
      ortho.position.copy(persp.position);
      ortho.up.copy(persp.up);
      const distance = persp.position.distanceTo(target);
      this.sizeOrtho(orthoHalfHeight(persp.fov, distance));
      this.camera = ortho;
    } else {
      persp.up.copy(ortho.up);
      const halfHeight = (ortho.top - ortho.bottom) / 2 / ortho.zoom;
      // Not the orthographic near plane: that one may be negative.
      const distance = Math.max(halfHeight / Math.tan(THREE.MathUtils.degToRad(persp.fov) / 2), 0.02);
      const direction = ortho.position.clone().sub(target);
      if (direction.lengthSq() === 0) direction.copy(OBLIQUE_DIRECTION);
      direction.normalize();
      persp.position.copy(target).addScaledVector(direction, distance);
      persp.updateProjectionMatrix();
      this.camera = persp;
    }

    this.controls.object = this.camera;
    this.projection = next;
    this.controls.update();
    // The planes belong to the projection: an orthographic slab spans the
    // whole scene, a perspective frustum starts in front of the camera.
    this.fitClipPlanes();
  }

  /**
   * Reorient the camera to a named axis-aligned view (Top / Front / …),
   * framing the whole loaded scene along that axis.
   *
   * Orbit-only: inside a panorama there is no scene to frame, and the page
   * leaves the panorama before asking for a preset. The orientation convention
   * is the CLI's — see {@link presetOrientation}.
   */
  setView(preset: ViewPreset): void {
    if (this.saved) return;
    this.following = false;
    // Locked to the plan, every preset is the plan: re-frame from the top.
    if (this.planLocked) {
      this.frameFromDirection(...planOrientation());
      return;
    }
    const { direction, up } = presetOrientation(preset);
    this.frameFromDirection(direction, up);
  }

  // --- locked plan (frontend-ayw.1) ----------------------------------------

  /** Whether the camera is locked to the top-down orthographic plan. */
  isPlanLocked(): boolean {
    return this.planLocked;
  }

  /**
   * Lock the camera to the plan — orthographic, straight down (the `top`
   * preset), rotation off so a drag pans and the wheel zooms — or unlock it
   * and put back the camera and projection it had before.
   *
   * Inside a panorama the request waits for the way out
   * ({@link exitFirstPerson}), which already returns to the camera the
   * panorama was entered from — the plan, when it was entered from there.
   */
  setPlanLock(locked: boolean): void {
    if (this.saved) {
      this.pendingPlanLock = locked;
      return;
    }
    this.pendingPlanLock = null;
    if (locked === this.planLocked) return;
    if (locked) {
      const ortho = this.orthographicCamera;
      this.planRestore = {
        position: this.camera.position.clone(),
        target: this.controls.target.clone(),
        up: this.camera.up.clone(),
        projection: this.projection,
        takenUnder: this.projection,
        orthoHalfHeight: (ortho.top - ortho.bottom) / 2,
        orthoZoom: ortho.zoom,
      };
      this.planLocked = true;
      // `following` is left as it is: a cloud still streaming in keeps
      // re-framing, now from the top (`homeOrientation`).
      this.applyProjection('orthographic');
      this.frameFromDirection(...planOrientation());
    } else {
      const restore = this.planRestore;
      this.planLocked = false;
      this.planRestore = null;
      if (restore) {
        // Put back the pose under the projection it was taken in (its
        // frustum is only valid there), then switch to the projection asked
        // for while locked, if another — rebuilt from that restored pose.
        for (const cam of [this.perspectiveCamera, this.orthographicCamera]) {
          cam.position.copy(restore.position);
          cam.up.copy(restore.up);
        }
        this.controls.target.copy(restore.target);
        if (restore.takenUnder === 'orthographic') {
          this.sizeOrtho(restore.orthoHalfHeight);
          this.orthographicCamera.zoom = restore.orthoZoom;
          this.orthographicCamera.updateProjectionMatrix();
          this.camera = this.orthographicCamera;
        } else {
          this.camera = this.perspectiveCamera;
        }
        this.projection = restore.takenUnder;
        this.controls.object = this.camera;
        this.perspectiveCamera.updateProjectionMatrix();
        this.applyProjection(restore.projection);
      } else {
        // No pose to go back to: the plan's +Y up is only for the plan.
        for (const cam of [this.perspectiveCamera, this.orthographicCamera]) cam.up.copy(WORLD_UP);
        this.applyProjection('perspective');
        this.frameFromDirection(OBLIQUE_DIRECTION, WORLD_UP);
      }
      this.controls.update();
      this.fitClipPlanes();
    }
    this.applyControlMapping();
  }

  /**
   * The orbit controls' buttons for the state the camera is in. Orbiting:
   * left rotates, middle dollies, right pans; one finger rotates, two dolly
   * and pan. Locked to the plan: rotation is off, left and right drag both
   * pan, middle (and the wheel) zooms; one finger pans, two pinch-zoom and
   * pan. Inside a panorama the look-around needs rotation, whatever the lock.
   */
  private applyControlMapping(): void {
    const locked = this.planLocked && !this.saved;
    const c = this.controls;
    c.enableRotate = !locked;
    c.mouseButtons = locked
      ? { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN }
      : { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
    c.touches = locked ? { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_PAN } : { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
  }

  /** Where the automatic framing looks from: straight down in the plan, else oblique. */
  private homeOrientation(): [THREE.Vector3, THREE.Vector3] {
    return this.planLocked ? planOrientation() : [OBLIQUE_DIRECTION, WORLD_UP];
  }

  // --- horizontal section (frontend-ayw.2) ---------------------------------

  /**
   * Cut everything above world height @p worldZ away (the locked plan's
   * storey section), or nothing with null. Combined with the clipping box's
   * planes; applied once the recentring origin is known.
   */
  setSectionHeight(worldZ: number | null): void {
    this.sectionWorldZ = worldZ !== null && Number.isFinite(worldZ) ? worldZ : null;
    this.applyClipping();
  }

  private applyClipping(): void {
    // Keeps z ≤ cut: three.js discards the negative side, and
    // (0, 0, −1)·p + cut is negative above it.
    this.sectionPlane =
      this.sectionWorldZ !== null && this.origin ? new THREE.Plane(new THREE.Vector3(0, 0, -1), this.sectionWorldZ - this.origin.z) : null;
    this.renderer.clippingPlanes = this.sectionPlane ? [...this.boxPlanes, this.sectionPlane] : this.boxPlanes;
  }

  // --- lighting (#443) -----------------------------------------------------

  /** Current key/fill light settings. */
  currentLighting(): LightingState {
    return { ...this.lighting };
  }

  /** Update one or more lighting parameters and drive the scene lights. */
  setLighting(next: Partial<LightingState>): void {
    this.lighting = { ...this.lighting, ...next };
    this.applyLighting();
  }

  private applyLighting(): void {
    this.hemisphere.intensity = this.lighting.ambientIntensity;
    this.keyLight.intensity = this.lighting.keyIntensity;
    this.keyLight.position.copy(lightDirection(this.lighting.azimuth, this.lighting.elevation));
  }

  /** Size the orthographic frustum to a half-height, keeping the aspect ratio. */
  private sizeOrtho(halfHeight: number): void {
    const ortho = this.orthographicCamera;
    const half = Math.max(halfHeight, 1e-3);
    ortho.top = half;
    ortho.bottom = -half;
    ortho.left = -half * this.perspectiveCamera.aspect;
    ortho.right = half * this.perspectiveCamera.aspect;
    ortho.zoom = 1;
    ortho.updateProjectionMatrix();
  }

  /** True when at least one point has been added. */
  hasContent(): boolean {
    return !this.bounds.isEmpty();
  }

  /**
   * The scene root, so a sibling layer can draw with this camera (#322).
   *
   * The Gaussian-splat layer is the caller: it renders through its own shader
   * but must share the camera and `OrbitControls`, or the viewport would have
   * two cameras the user has to orbit separately.
   */
  sceneRoot(): THREE.Scene {
    return this.scene;
  }

  /**
   * A second scene drawn after {@link sceneRoot} with the clipping planes off,
   * in the same scene-local frame — for gizmos that must stay whole while the
   * content is clipped.
   */
  overlayRoot(): THREE.Scene {
    return this.overlay;
  }

  /**
   * Register world-space bounds for content this scene draws but does not own,
   * and get back the offset that content must be positioned by.
   *
   * Two things at once because they are two halves of the same fact. The
   * recentring described above applies to *everything* in this scene, not just
   * to points: a splat left at its georeferenced coordinates while the cloud is
   * recentred lands kilometres away. And bounds a layer never contributed are
   * bounds `frameAll` cannot frame — which is exactly the splat-only case,
   * where no page has run `growBounds`.
   *
   * @param box World-space bounds, in the same frame the point pages arrive in.
   * @returns The recentring origin, adopted from @p box if none is set yet.
   */
  registerExternalBounds(box: THREE.Box3): THREE.Vector3 {
    if (!this.origin) this.origin = box.getCenter(new THREE.Vector3());
    this.bounds.union(box);
    return this.origin.clone();
  }

  /**
   * World -> scene coordinates, adopting @p world as the recentring origin if
   * none is set yet.
   *
   * The lighter half of {@link registerExternalBounds}: a panorama's sphere is
   * 20 m of backdrop that must not enter the framing bounds, but its centre
   * still has to be recentred like everything else or it lands at the scan's
   * absolute coordinates. Callers whose content *should* be framable use
   * `registerExternalBounds` instead.
   */
  toSceneLocal(world: THREE.Vector3): THREE.Vector3 {
    if (!this.origin) this.origin = world.clone();
    return world.clone().sub(this.origin);
  }

  /**
   * Current camera frustum, position, and recentring origin.
   *
   * Passed to {@link useCloudStream} so it can frustum-cull the tile index and
   * fetch only what the camera sees. The frustum is computed from the camera's
   * current projection × view matrices — the render loop keeps them current, so
   * this is always valid to call during a camera-change callback.
   */
  getCameraState(): {
    frustum: THREE.Frustum;
    position: THREE.Vector3;
    origin: THREE.Vector3 | null;
  } {
    this.camera.updateWorldMatrix(true, false);
    const projScreenMatrix = new THREE.Matrix4().multiplyMatrices(
      this.camera.projectionMatrix,
      this.camera.matrixWorldInverse,
    );
    return {
      frustum: new THREE.Frustum().setFromProjectionMatrix(projScreenMatrix),
      position: this.camera.position.clone(),
      origin: this.origin,
    };
  }

  /**
   * Register a listener that fires whenever the orbit camera moves.
   *
   * Returns a cleanup function that removes the listener. Used by the tile
   * streaming path to dispatch `rux-camera-move` so {@link useCloudStream}
   * can re-evaluate the visible tile set.
   */
  onCameraChange(listener: () => void): () => void {
    this.controls.addEventListener('change', listener);
    return () => this.controls.removeEventListener('change', listener);
  }

  /**
   * Call `listener` when the user starts moving the camera (the controls'
   * `start`: a drag, a wheel, a touch) — never for a programmatic move.
   */
  onUserCamera(listener: () => void): () => void {
    this.controls.addEventListener('start', listener);
    return () => this.controls.removeEventListener('start', listener);
  }

  /**
   * Objects under the pointer, nearest first.
   *
   * Exposed so a sibling layer can be clickable without a second camera or a
   * copy of this one: picking needs the camera that drew the frame, and that
   * camera stays private.
   *
   * @param ndc Pointer position in normalised device coordinates (-1..1).
   */
  pick(ndc: THREE.Vector2, objects: THREE.Object3D[]): THREE.Intersection[] {
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, this.camera);
    return raycaster.intersectObjects(objects, false);
  }

  /**
   * Create a raycaster aimed at @p ndc (NDC) using the current camera.
   *
   * Exported so callers (e.g. pose-graph edge picking) can reuse the same
   * camera setup without needing access to the private `camera` field.
   *
   * @param ndc Pointer position in normalised device coordinates (-1..1).
   */
  raycasterAt(ndc: THREE.Vector2): THREE.Raycaster {
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, this.camera);
    return raycaster;
  }

  /**
   * Pick the nearest point-cloud point under the pointer and return its
   * world-space position, or `null` when nothing is hit.
   *
   * Uses the same camera as {@link pick}: the raycaster needs the camera that
   * drew the frame. The threshold is two point radii in world units — large
   * enough to be usable at typical point densities, small enough not to snap
   * across large gaps.
   *
   * The hit position is in **world** coordinates (scene-local + recentring
   * origin), so it can be passed directly to the visibility API which speaks
   * the same world frame as the stored sensor poses.
   *
   * @param ndc Pointer position in normalised device coordinates (-1..1).
   */
  pickCloudPoint(ndc: THREE.Vector2): THREE.Vector3 | null {
    if (!this.origin) return null;

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, this.camera);
    raycaster.params.Points = {
      threshold: this.pointSizeUnit === 'm' ? this.pointSize * 2 : PX_PICK_THRESHOLD,
    };

    // Solid points first: a labelled point seen through a ghost one is what
    // the user is pointing at. Ghost points answer only where nothing solid
    // is under the pointer (raycasting honours each pass's draw range).
    const solid: THREE.Object3D[] = [];
    const ghost: THREE.Object3D[] = [];
    for (const layer of this.layers.values()) {
      for (const page of layer.pages) {
        solid.push(page.points);
        if (page.ghostPoints.visible) ghost.push(page.ghostPoints);
      }
    }

    // Raycasting knows nothing of the clipping planes: a hit the clipping box
    // or the plan's section cut away is not on screen and must not be picked.
    const planes = this.renderer.clippingPlanes;
    const shown = (list: THREE.Intersection[]) =>
      planes.length === 0 ? list : list.filter((h) => planes.every((pl) => pl.distanceToPoint(h.point) >= 0));
    let hits = shown(raycaster.intersectObjects(solid, false));
    if (hits.length === 0) hits = shown(raycaster.intersectObjects(ghost, false));
    if (hits.length === 0) return null;

    // Convert scene-local hit back to world coordinates.
    return hits[0].point.clone().add(this.origin);
  }

  /**
   * The cloud passes a snap may land on (the measure tools, `snapProbe.ts`):
   * every shown layer's solid points, and its ghost points where drawn.
   */
  snapClouds(): THREE.Points[] {
    const out: THREE.Points[] = [];
    for (const layer of this.layers.values()) {
      if (!layer.group.visible) continue;
      for (const page of layer.pages) {
        out.push(page.points);
        if (page.ghostPoints.visible) out.push(page.ghostPoints);
      }
    }
    return out;
  }

  /** The clipping planes in force now (the box and the plan's section), scene-local. */
  activeClippingPlanes(): readonly THREE.Plane[] {
    return this.renderer.clippingPlanes;
  }

  /**
   * The pick ray under @p ndc in **world** coordinates (origin and unit
   * direction), or null before any content has set the recentring origin.
   * Under the orthographic plan the origin sits on the near plane and the
   * direction is the view axis, so a plane hit is exact whatever the zoom.
   */
  worldRayAt(ndc: THREE.Vector2): { origin: [number, number, number]; dir: [number, number, number] } | null {
    if (!this.origin) return null;
    const ray = this.raycasterAt(ndc).ray;
    const o = ray.origin.clone().add(this.origin);
    return { origin: [o.x, o.y, o.z], dir: [ray.direction.x, ray.direction.y, ray.direction.z] };
  }

  /** The recentring origin (world coordinates of scene 0,0,0), or null before any content. */
  worldOrigin(): Rgb | null {
    return this.origin ? [this.origin.x, this.origin.y, this.origin.z] : null;
  }

  /**
   * Project world points (xyz stride 3) to canvas CSS pixels (xy stride 2)
   * with the current camera. A point behind the camera, outside the depth
   * range, cut away by the clipping box, or given before any content has set
   * the origin comes back as `NaN, NaN` — "not on screen", which hit testing
   * and label placement both skip.
   *
   * Meant for a few hundred points per pointer event or camera change
   * (instance centroids, room labels), not per frame per point.
   *
   * @param clip false projects what the clipping planes cut away too — for
   *   chrome drawn over the model, like the plan's spaces and markers.
   */
  projectToScreen(world: ArrayLike<number>, clip = true): Float32Array {
    const n = Math.floor(world.length / 3);
    const out = new Float32Array(n * 2).fill(Number.NaN);
    if (!this.origin) return out;
    const camera = this.camera;
    camera.updateMatrixWorld();
    const matrix = new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    const planes = clip ? this.renderer.clippingPlanes : [];
    const v = new THREE.Vector3();
    for (let i = 0; i < n; i += 1) {
      const x = world[i * 3];
      const y = world[i * 3 + 1];
      const z = world[i * 3 + 2];
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
      v.set(x - this.origin.x, y - this.origin.y, z - this.origin.z);
      let clipped = false;
      for (const plane of planes) {
        if (plane.distanceToPoint(v) < 0) {
          clipped = true;
          break;
        }
      }
      if (clipped) continue;
      v.applyMatrix4(matrix);
      if (v.z < -1 || v.z > 1) continue;
      out[i * 2] = ((v.x + 1) / 2) * width;
      out[i * 2 + 1] = ((1 - v.y) / 2) * height;
    }
    return out;
  }

  /**
   * Frame a world-space box (a room's extent) from the current view
   * direction, so focusing a room reads as moving closer rather than as a cut
   * to an unrelated angle. Instant — no animation to respect or skip.
   */
  frameWorldBox(min: { x: number; y: number; z: number }, max: { x: number; y: number; z: number }): void {
    if (!this.origin) return;
    const box = new THREE.Box3(new THREE.Vector3(min.x, min.y, min.z), new THREE.Vector3(max.x, max.y, max.z));
    if (box.isEmpty()) return;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    sphere.center.sub(this.origin);
    const direction = this.camera.position.clone().sub(this.controls.target);
    if (direction.lengthSq() === 0) direction.copy(OBLIQUE_DIRECTION);
    this.following = false;
    // The planes are refit from the whole scene's bounds every frame, so the
    // rest of the building stays drawn behind the focused room.
    this.frameSphere(sphere, direction, this.camera.up.clone());
  }

  /**
   * Render one frame now and read it back as an image (the `?captureFor=`
   * thumbnail). Rendering in the same task as `toBlob` is what makes the read
   * valid without `preserveDrawingBuffer`.
   */
  capture(type = 'image/png'): Promise<Blob | null> {
    this.controls.update();
    this.fitClipPlanes();
    this.renderer.render(this.scene, this.camera);
    return new Promise((resolve) => this.canvas.toBlob(resolve, type));
  }

  /**
   * Stand the camera at @p position looking along @p forward (both in scene
   * coordinates), and remember where it was.
   *
   * This is panorama mode's camera: `OrbitControls` orbiting a target 10 cm
   * ahead is a look-around, and inverting `rotateSpeed` makes a drag grab the
   * image rather than swing around it — the motion every 360 viewer uses.
   * Panning is switched off because there is nowhere to pan to from inside a
   * sphere, and dollying because it would push the camera through the wall.
   *
   * Idempotent: entering twice does not overwrite the saved orbit pose with
   * the immersive one, so stepping panorama-to-panorama still returns the user
   * to where they were standing.
   */
  enterFirstPerson(position: THREE.Vector3, forward: THREE.Vector3): void {
    this.following = false;
    if (!this.saved) {
      this.saved = {
        position: this.camera.position.clone(),
        target: this.controls.target.clone(),
        up: this.camera.up.clone(),
        near: this.perspectiveCamera.near,
        far: this.perspectiveCamera.far,
        fov: this.perspectiveCamera.fov,
        projection: this.projection,
      };
    }

    // A 360 look-around is perspective by nature; force it here and restore the
    // orthographic choice, if any, on exit.
    this.camera = this.perspectiveCamera;
    this.controls.object = this.perspectiveCamera;
    this.projection = 'perspective';

    const persp = this.perspectiveCamera;
    const direction = forward.lengthSq() > 0 ? forward.clone().normalize() : FORWARD_X.clone();
    persp.position.copy(position);
    persp.up.copy(WORLD_UP);
    persp.near = 0.01;
    persp.far = PANORAMA_RADIUS * 4;
    persp.updateProjectionMatrix();

    this.controls.target.copy(position).addScaledVector(direction, 0.1);
    this.controls.enablePan = false;
    this.controls.enableZoom = false;
    this.controls.rotateSpeed = -0.3;
    this.applyControlMapping();
    this.controls.update();
  }

  /** Put the orbit camera back where {@link enterFirstPerson} found it. */
  exitFirstPerson(): void {
    this.controls.enablePan = true;
    this.controls.enableZoom = true;
    this.controls.rotateSpeed = 1;

    const saved = this.saved;
    this.saved = null;
    if (!saved) return;

    const persp = this.perspectiveCamera;
    persp.position.copy(saved.position);
    persp.up.copy(saved.up);
    persp.near = saved.near;
    persp.far = saved.far;
    persp.fov = saved.fov;
    persp.updateProjectionMatrix();

    this.camera = persp;
    this.controls.object = persp;
    this.projection = 'perspective';
    this.controls.target.copy(saved.target);
    this.controls.update();

    // Rebuild the orthographic projection the user had before, from the
    // now-restored perspective pose so the framing carries across.
    if (saved.projection === 'orthographic') this.applyProjection('orthographic');
    this.applyControlMapping();
    // A lock or unlock asked for while inside the panorama.
    if (this.pendingPlanLock !== null) {
      const pending = this.pendingPlanLock;
      this.pendingPlanLock = null;
      this.setPlanLock(pending);
    }
    // The saved planes date from when the panorama was entered — the bounds
    // may have grown since — so fit them to the scene as it is now.
    this.fitClipPlanes();
  }

  // --- clipping box (#444) -------------------------------------------------

  /**
   * Scene-local bounds of all loaded content, or `null` if nothing has been
   * added yet.
   *
   * "Scene-local" means the recentred frame: world coordinates minus the
   * recentring `origin`, the same frame every `THREE.Object3D` in the scene
   * lives in. The returned box is a copy — mutating it has no effect here.
   */
  getSceneBounds(): THREE.Box3 | null {
    if (this.bounds.isEmpty()) return null;
    if (!this.origin) return this.bounds.clone();
    return this.bounds.clone().translate(this.origin.clone().negate());
  }

  /**
   * Apply a set of world-space clipping planes to the renderer.
   *
   * `renderer.clippingPlanes` clips all geometry globally without requiring
   * per-material opt-in (`localClippingEnabled`). Pass an empty array to
   * disable clipping.
   */
  setClippingPlanes(planes: THREE.Plane[]): void {
    this.boxPlanes = planes;
    this.applyClipping();
  }

  /** Enable or disable orbit-camera interaction without disposing the controls. */
  setControlsEnabled(enabled: boolean): void {
    this.controls.enabled = enabled;
  }

  /**
   * The active camera, for use by scene layers that need to construct a ray.
   *
   * Exposed narrowly so `ClippingBoxLayer` can compute a drag plane without
   * keeping its own camera reference.
   */
  getCamera(): THREE.PerspectiveCamera | THREE.OrthographicCamera {
    return this.camera;
  }

  /** The canvas element the renderer draws into. */
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  removeLayer(layerId: string): void {
    const layer = this.layers.get(layerId);
    if (!layer) return;
    for (const page of layer.pages) {
      disposePoints(page.points);
      disposePoints(page.ghostPoints);
    }
    this.scene.remove(layer.group);
    this.layers.delete(layerId);
  }

  /** Drop every layer and reset the recentring origin. */
  clear(): void {
    for (const id of [...this.layers.keys()]) this.removeLayer(id);
    this.bounds.makeEmpty();
    this.origin = null;
    this.applyClipping();
    this.following = false;
    this.followedRadius = 0;
  }

  private growBounds(positions: Float32Array): void {
    const point = new THREE.Vector3();
    for (let i = 0; i < positions.length; i += 3) {
      point.set(positions[i], positions[i + 1], positions[i + 2]);
      this.bounds.expandByPoint(point);
    }
  }

  /**
   * Move the camera so the whole loaded cloud is in frame.
   *
   * @param follow Keep reframing as the bounds grow until the user takes the
   *   camera — the automatic framing on first content, whose bounds are only
   *   the first streamed page's.
   */
  frameAll(follow = false): void {
    // Approach from an oblique angle rather than an axis: an axis-aligned view
    // of a corridor scan is a wall of points with no depth cue at all. The
    // locked plan frames from the top.
    this.frameFromDirection(...this.homeOrientation());
    this.following = follow;
    this.followedRadius = this.bounds.isEmpty() ? 0 : this.bounds.getBoundingSphere(new THREE.Sphere()).radius;
    this.lastFollowAt = performance.now();
  }

  /**
   * Frame the whole loaded scene along a given view direction and up vector.
   *
   * Shared by {@link frameAll} (oblique) and {@link setView} (axis presets).
   * @p direction points from the framed centre toward the camera.
   */
  private frameFromDirection(direction: THREE.Vector3, up: THREE.Vector3): void {
    if (this.bounds.isEmpty() || !this.origin) return;

    const sphere = new THREE.Sphere();
    this.bounds.getBoundingSphere(sphere);
    sphere.center.sub(this.origin);
    this.frameSphere(sphere, direction, up);
  }

  /**
   * Frame a scene-local sphere along @p direction (centre → camera). The
   * near/far planes are not set here: {@link fitClipPlanes} fits them to the
   * whole scene every frame.
   */
  private frameSphere(sphere: THREE.Sphere, direction: THREE.Vector3, up: THREE.Vector3): void {
    const radius = Math.max(sphere.radius, 0.5);
    const fov = THREE.MathUtils.degToRad(this.perspectiveCamera.fov);
    const distance = (radius / Math.sin(fov / 2)) * 1.15;

    const dir = direction.lengthSq() > 0 ? direction.clone().normalize() : OBLIQUE_DIRECTION.clone();
    const position = sphere.center.clone().addScaledVector(dir, distance);

    // In panorama mode the camera is standing inside a sphere, and the framing
    // request is almost always the cloud's own first page arriving a moment
    // after the backdrop. Flying out of the picture the user asked for would
    // be the wrong reading of it — so the framed pose is written into the
    // remembered orbit camera, and exiting lands on it.
    if (this.saved) {
      this.saved.position.copy(position);
      this.saved.target.copy(sphere.center);
      this.saved.up.copy(up);
      return;
    }

    for (const cam of [this.perspectiveCamera, this.orthographicCamera]) {
      cam.position.copy(position);
      cam.up.copy(up);
    }
    // The orthographic camera holds the same bounding sphere with the same 15%
    // margin the perspective distance uses, so a toggle after framing is seamless.
    this.sizeOrtho(radius * 1.15);
    this.perspectiveCamera.updateProjectionMatrix();

    this.controls.target.copy(sphere.center);
    this.controls.update();
    this.fitClipPlanes();
  }

  // --- camera snapshot (frontend-38f) --------------------------------------

  /**
   * The orbit camera as plain numbers, to outlive this scene: the Ground
   * keeps it across a route switch and puts it back on the next scene
   * ({@link restoreCamera}). Under the locked plan it is the camera from
   * before the lock (the plan itself is re-derived from the setting); inside
   * a panorama, the orbit camera it was entered from. Null before there is a
   * recentring origin.
   */
  cameraSnapshot(): CameraSnapshot | null {
    if (!this.origin) return null;
    const ortho = this.orthographicCamera;
    const vec = (v: THREE.Vector3): [number, number, number] => [v.x, v.y, v.z];
    const restore = this.planLocked && !this.saved ? this.planRestore : null;
    if (this.planLocked && !this.saved && !restore) return null;
    const saved = this.saved;
    const snapshot: CameraSnapshot = saved
      ? {
          origin: vec(this.origin),
          projection: saved.projection,
          position: vec(saved.position),
          target: vec(saved.target),
          up: vec(saved.up),
          orthoHalfHeight: (ortho.top - ortho.bottom) / 2,
          orthoZoom: ortho.zoom,
        }
      : restore
      ? {
          origin: vec(this.origin),
          projection: restore.takenUnder,
          position: vec(restore.position),
          target: vec(restore.target),
          up: vec(restore.up),
          orthoHalfHeight: restore.orthoHalfHeight,
          orthoZoom: restore.orthoZoom,
        }
      : {
          origin: vec(this.origin),
          projection: this.projection,
          position: vec(this.camera.position),
          target: vec(this.controls.target),
          up: vec(this.camera.up),
          orthoHalfHeight: (ortho.top - ortho.bottom) / 2,
          orthoZoom: ortho.zoom,
        };
    return isCameraSnapshot(snapshot) ? snapshot : null;
  }

  /**
   * Put back a {@link cameraSnapshot} on a fresh scene, before any content:
   * the snapshot's recentring origin is adopted (so the pose, the clipping
   * box and everything else scene-local mean what they meant), and the
   * automatic first framing is skipped by the caller. False when the scene
   * already has an origin of its own or the snapshot is unusable.
   */
  restoreCamera(snapshot: CameraSnapshot): boolean {
    if (this.origin || this.saved || !isCameraSnapshot(snapshot)) return false;
    this.origin = new THREE.Vector3(...snapshot.origin);
    for (const cam of [this.perspectiveCamera, this.orthographicCamera]) {
      cam.position.set(...snapshot.position);
      cam.up.set(...snapshot.up);
    }
    this.controls.target.set(...snapshot.target);
    if (snapshot.projection === 'orthographic') {
      this.sizeOrtho(snapshot.orthoHalfHeight);
      this.orthographicCamera.zoom = snapshot.orthoZoom;
      this.orthographicCamera.updateProjectionMatrix();
      this.camera = this.orthographicCamera;
    } else {
      this.camera = this.perspectiveCamera;
    }
    this.perspectiveCamera.updateProjectionMatrix();
    this.projection = snapshot.projection;
    this.controls.object = this.camera;
    this.following = false;
    this.controls.update();
    this.applyClipping();
    this.fitClipPlanes();
    return true;
  }

  dispose(): void {
    this.disposed = true;
    if (this.frameHandle !== null) cancelAnimationFrame(this.frameHandle);
    this.resizeObserver?.disconnect();
    this.clear();
    this.scene.remove(this.hemisphere);
    this.scene.remove(this.keyLight);
    this.hemisphere.dispose();
    this.keyLight.dispose();
    this.controls.removeEventListener('start', this.stopFollowing);
    this.controls.dispose();
    this.renderer.dispose();
  }
}

function centroidOf(positions: Float32Array): THREE.Vector3 {
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    minX = Math.min(minX, positions[i]);
    maxX = Math.max(maxX, positions[i]);
    minY = Math.min(minY, positions[i + 1]);
    maxY = Math.max(maxY, positions[i + 1]);
    minZ = Math.min(minZ, positions[i + 2]);
    maxZ = Math.max(maxZ, positions[i + 2]);
  }
  return new THREE.Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
}

function recentre(positions: Float32Array, origin: THREE.Vector3): Float32Array {
  const out = new Float32Array(positions.length);
  for (let i = 0; i < positions.length; i += 3) {
    out[i] = positions[i] - origin.x;
    out[i + 1] = positions[i + 1] - origin.y;
    out[i + 2] = positions[i + 2] - origin.z;
  }
  return out;
}

function disposePoints(points: THREE.Points): void {
  points.geometry.dispose();
  const material = points.material;
  if (Array.isArray(material)) material.forEach((entry) => entry.dispose());
  else material.dispose();
}
